import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatSAR } from "../../../helper/format";
import MDAvatar from "components/MDAvatar";
import Axios from "axios";
import Cookies from "js-cookie";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  useProducts,
  useDeleteProduct,
  useUpdateProduct,
  useRefetchProducts,
} from "api/useProducts.ts";
import { useCategories } from "api/useCategories.ts";
import { useMosques } from "api/useMosques.ts";

export default function productsTableData(packFilter = "all") {
  const { t } = useTranslation();
  const { data: fetchedProducts, isLoading } = useProducts();
  const deleteProduct = useDeleteProduct();
  const updateProduct = useUpdateProduct();
  const { data: mosques } = useMosques();
  const { refetch: refetchProducts } = useRefetchProducts();
  const { data: categories } = useCategories();
  const [products, setProducts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [editedProduct, setEditedProduct] = useState({
    productId: "",
    productName: {
      en: "",
      ar: "",
    },
    price: 0,
    stockQuantity: 0,
    category: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
    isPack: false,
    packProduct: "",
    packQuantity: 1,
    minSupplyQuantity: 0,
  });

  const handleFileChange = (e) => {
    setImagePreview(URL.createObjectURL(e.target.files[0]));
    setSelectedFile(e.target.files[0]);
  };

  const handleOpenFileModal = (productId) => {
    setSelectedId(productId);
    setSelectedFile(null);
    setIsFileModalOpen(true);
  };
  const closeFileModal = () => {
    setIsFileModalOpen(false);
    setSelectedFile(null);
  };
  const handleUploadImage = async () => {
    if (!selectedFile) {
      setNotification({
        open: true,
        message: t("common.noFileSelected"),
        severity: "error",
      });
      return;
    }
    setIsUploading(true);
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Unauthorized access");
      }
      const response = await Axios.patch(
        `${process.env.REACT_APP_API_URL}/products/update-image/${selectedId}`,
        { photo: selectedFile },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNotification({
        open: true,
        message: t("products.imageUploaded"),
        severity: "success",
      });
      setImagePreview(null);
      setSelectedFile(null);
      refetchProducts();
      closeFileModal();
    } catch (error) {
      console.error("Error uploading image:", error.response || error.message);
      setNotification({
        open: true,
        message: t("products.imageUploadFailed"),
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openDeleteModal = (productId) => {
    setSelectedId(productId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleEditClick = (product) => {
    setEditedProduct({
      productId: product._id,
      productName: product?.name,
      price: product.price,
      stockQuantity: product.quantity,
      category: product.category,
      description: product.description,
      isPack: product.isPack || false,
      packProduct:
        typeof product.packProduct === "object"
          ? product.packProduct?._id
          : product.packProduct || "",
      packQuantity: product.packQuantity || 1,
      minSupplyQuantity: product.isPack ? 0 : product.minSupplyQuantity || 0,
    });
    editedProduct && setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditedProduct({
      productId: "",
      productName: {
        en: "",
        ar: "",
      },
      price: 0,
      stockQuantity: 0,
      category: {
        _id: "",
        name: {
          ar: "",
          en: "",
        },
      },
      description: {
        en: "",
        ar: "",
      },
      isPack: false,
      packProduct: "",
      packQuantity: 1,
      minSupplyQuantity: 0,
    });
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const keys = name.split(".");
    if (keys.length === 2) {
      setEditedProduct((prev) => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: value,
        },
      }));
    } else {
      setEditedProduct((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedId) return;

    deleteProduct.mutate(selectedId, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("products.productDeleted"),
          severity: "success",
        });
        closeDeleteModal();
      },
      onError: (error) => {
        console.error("Error deleting product:", error);
        setNotification({
          open: true,
          message: t("products.productDeleteFailed"),
          severity: "error",
        });
      },
    });
  };
  const handleSaveChanges = async () => {
    // Validation for pack fields
    if (editedProduct.isPack) {
      if (!editedProduct.packProduct) {
        setNotification({
          open: true,
          message: t("products.validation.selectProductForPack"),
          severity: "error",
        });
        return;
      }
      if (!editedProduct.packQuantity || editedProduct.packQuantity < 1) {
        setNotification({
          open: true,
          message: t("products.validation.packQuantityMin"),
          severity: "error",
        });
        return;
      }
      if (editedProduct.packProduct === editedProduct.productId) {
        setNotification({
          open: true,
          message: t("products.validation.packCannotBeSelf"),
          severity: "error",
        });
        return;
      }
    }

    const originalProduct = products.find((p) => p._id === editedProduct.productId);

    const updatedFields = Object.keys(editedProduct).reduce((changes, key) => {
      if (editedProduct[key] !== originalProduct[key]) {
        changes[key] = editedProduct[key];
      }
      return changes;
    }, {});

    if (Object.keys(updatedFields).length === 0) {
      setNotification({
        open: true,
        message: t("common.noChanges"),
        severity: "info",
      });
      return;
    }
    const updatedProduct = {
      _id: editedProduct.productId,
      name: editedProduct.productName,
      price: editedProduct.price,
      category: editedProduct.category._id || editedProduct.category,
      quantity: editedProduct.stockQuantity,
      description: editedProduct.description,
      isPack: editedProduct.isPack,
      ...(editedProduct.isPack && {
        packProduct: editedProduct.packProduct,
        packQuantity: parseInt(editedProduct.packQuantity),
      }),
      ...(!editedProduct.isPack && {
        minSupplyQuantity: parseInt(editedProduct.minSupplyQuantity),
      }),
    };

    updateProduct.mutate(updatedProduct, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("products.productUpdated"),
          severity: "success",
        });
        handleCloseModal();
      },
      onError: (error) => {
        console.error("Error updating product:", error);
        setNotification({
          open: true,
          message: t("products.productUpdateFailed"),
          severity: "error",
        });
      },
    });
  };

  useEffect(() => {
    !isLoading && setProducts(fetchedProducts);
  }, [fetchedProducts, isLoading]);

  useEffect(() => {}, [mosques]);

  const rows = useMemo(() => {
    return isLoading
      ? Array(10)
          .fill(0)
          .map((_, index) => ({
            productId: index + 1,
            productName: t("common.loading"),
            productimg: <MDAvatar size="xl" />,
            category: t("common.loading"),
            description: t("common.loading"),
            price: t("common.loading"),
            stockQuantity: t("common.loading"),
            releaseDate: t("common.loading"),
            action: <div className="flex justify-center space-x-2">{t("common.loading")}</div>,
          }))
      : Array.isArray(products) &&
          products
            .filter((product) => {
              if (packFilter === "packs") return product.isPack;
              if (packFilter === "non-packs") return !product.isPack;
              return true; // "all"
            })
            .map((product) => ({
              productId: product._id,
              productName: (
                <div className="max-w-[100px] truncate">
                  <p
                    className="font-semibold"
                    title={`${product?.name.en || ""} | ${product?.name.ar || ""}`}
                  >
                    {`${product?.name.en || ""} | ${product?.name.ar || ""}`}
                  </p>
                  {product.isPack && (
                    <>
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-1">
                        {t("products.pack")}
                      </span>
                      <p className="text-xs text-blue-600 mt-1">
                        {t("products.packOf")}:{" "}
                        {typeof product.packProduct === "object"
                          ? `${product.packProduct?.name?.en || ""} | ${
                              product.packProduct?.name?.ar || ""
                            }`
                          : t("products.product")}{" "}
                        × {product.packQuantity || 1}
                      </p>
                    </>
                  )}
                </div>
              ),
              productimg: (
                <MDAvatar
                  src={product.image || "https://via.placeholder.com/150"}
                  name={product?.name.en}
                  size="xl"
                />
              ),
              category: `${product.category?.name.en || ""} | ${product.category?.name.ar || ""}`,
              description: `${product.description.en || ""} | ${product.description.ar || ""}`,
              price: formatSAR(product.price),
              stockQuantity: product.quantity,
              minSupplyQuantity: product.isPack
                ? t("products.notApplicableForPacks")
                : product.minSupplyQuantity || 0,
              releaseDate: new Date(product.createdAt).toLocaleDateString(),
              action: (
                <div className="flex justify-center space-x-2">
                  <IconButton
                    color="primary"
                    size="medium"
                    onClick={() => handleEditClick(product)}
                  >
                    <EditIcon fontSize="medium" />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    size="medium"
                    onClick={() => handleOpenFileModal(product._id)}
                  >
                    <AddPhotoAlternateIcon fontSize="medium" />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="medium"
                    onClick={() => openDeleteModal(product._id)}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </div>
              ),
            }));
  }, [isLoading, products, packFilter]);

  return {
    columns: [
      { Header: t("products.columns.id"), accessor: "productId", align: "center" },
      { Header: t("products.columns.productImage"), accessor: "productimg", align: "center" },
      { Header: t("products.columns.productName"), accessor: "productName", align: "center" },
      { Header: t("products.columns.description"), accessor: "description", align: "center" },
      { Header: t("products.columns.category"), accessor: "category", align: "center" },
      { Header: t("products.columns.price"), accessor: "price", align: "center" },
      { Header: t("products.columns.stockQuantity"), accessor: "stockQuantity", align: "center" },
      {
        Header: t("products.columns.minSupplyQty"),
        accessor: "minSupplyQuantity",
        align: "center",
      },
      { Header: t("products.columns.releaseDate"), accessor: "releaseDate", align: "center" },
      { Header: t("products.columns.actions"), accessor: "action", align: "center" },
    ],
    rows,
    editedProduct,
    setEditedProduct,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteProduct,
    isDeleteModalOpen,
    closeDeleteModal,
    categories,
    handleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isUploading,
    fetchedProducts,
  };
}
